import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class LindyHeaderMenu extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  #abortController = new AbortController();

  #isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  /** @type {HTMLDivElement | null} */
  #backdrop = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.#setupClickOutside();
    this.#attachMenuClickHandlers();
    this.#createBackdrop();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.removeEventListener('click', this.#handleClickOutside);
    this.#removeBackdrop();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Public method: Activate menu item on hover (desktop only)
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#isTouchDevice) return;
    
    this.#cancelPendingActions();
    this.#debouncedActivateHandler(event);
  };

  /**
   * Public method: Deactivate menu item on leave (desktop only)
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#isTouchDevice) return;
    
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Internal: Handle activation with event processing
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#prepareForAnimation();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    this.#activateMenuItem(item);
  };

  /**
   * Internal: Activate a specific menu item
   * @param {HTMLElement} item
   */
  #activateMenuItem = (item) => {
    this.#closeActiveSubmenu();
    this.#openSubmenu(item);
  };

  /**
   * Internal: Close currently active submenu
   */
  #closeActiveSubmenu = () => {
    const activeItem = this.#state.activeItem;
    if (activeItem) {
      activeItem.ariaExpanded = 'false';
    }
  };

  /**
   * Internal: Open submenu for specific item
   * @param {HTMLElement} item
   */
  #openSubmenu = (item) => {
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);
    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    
    this.#showBackdrop();
  };

  /**
   * Internal: Prepare component for animation
   */
  #prepareForAnimation = () => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');
  };

  /**
   * Internal: Cancel all pending activation/deactivation
   */
  #cancelPendingActions = () => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Internal: Close submenu immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.#updateSubmenuStyles(0, 0);
    this.#updateAriaStates(item, false);
    this.#scheduleAnimationCleanup(item);

    this.#state.activeItem = null;
    this.#hideBackdrop();
  };

  /**
   * Internal: Update submenu CSS properties
   * @param {number} height
   * @param {number} opacity
   */
  #updateSubmenuStyles = (height, opacity) => {
    this.style.setProperty('--submenu-height', `${height}px`);
    this.style.setProperty('--submenu-opacity', `${opacity}`);
  };

  /**
   * Internal: Update ARIA states for accessibility
   * @param {HTMLElement} item
   * @param {boolean} isExpanded
   */
  #updateAriaStates = (item, isExpanded) => {
    this.ariaExpanded = String(isExpanded);
    item.ariaExpanded = String(isExpanded);
  };

  /**
   * Internal: Schedule animation cleanup after delay
   * @param {HTMLElement} item
   */
  #scheduleAnimationCleanup = (item) => {
    item.setAttribute('data-animating', '');
    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Setup click handlers for touch devices
   * Desktop uses hover (pointerenter/pointerleave)
   * Touch uses click with toggle behavior
   */
  #attachMenuClickHandlers = () => {
    if (!this.#isTouchDevice) return;
    
    const menuLinks = /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('a.menu-list__link'));
    menuLinks.forEach((link) => {
      link.addEventListener(
        'click',
        /** @param {MouseEvent} event */
        (event) => this.#handleMenuLinkClick(event, link),
        { signal: this.#abortController.signal }
      );
    });
  };

  /**
   * Handle click on menu link (touch devices only)
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  #handleMenuLinkClick = (event, link) => {
    const hasSubmenu = link.getAttribute('aria-haspopup') === 'true';
    
    if (hasSubmenu) {
      this.#handleSubmenuParentClick(event, link);
    } else {
      this.#handleRegularLinkClick();
    }
  };

  /**
   * Handle click on parent link with submenu
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  #handleSubmenuParentClick = (event, link) => {
    event.preventDefault();
    event.stopPropagation();

    const item = findMenuItem(link);
    if (!item) return;

    if (this.#state.activeItem === item) {
      this.#deactivate(item);
    } else {
      this.#cancelPendingActions();
      this.#prepareForAnimation();
      this.#activateMenuItem(item);
    }
  };

  /**
   * Handle click on regular link (no submenu)
   * Close any open submenu before navigation
   */
  #handleRegularLinkClick = () => {
    if (this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * Setup click outside listener to close active submenu
   */
  #setupClickOutside = () => {
    document.addEventListener('click', this.#handleClickOutside, { signal: this.#abortController.signal });
  };

  /**
   * Handle click outside menu to close active submenu
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (!this.#isTouchDevice || !this.#state.activeItem) return;

    if (!(event.target instanceof Element)) return;

    if (!this.contains(event.target) && !event.target.classList.contains('lindy-menu-backdrop')) {
      this.#deactivate();
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Create backdrop element for touch devices
   */
  #createBackdrop = () => {
    if (!this.#isTouchDevice) return;

    this.#backdrop = document.createElement('div');
    this.#backdrop.className = 'lindy-menu-backdrop';
    this.#backdrop.setAttribute('aria-hidden', 'true');
    
    this.#backdrop.addEventListener(
      'click',
      () => this.#deactivate(),
      { signal: this.#abortController.signal }
    );

    document.body.appendChild(this.#backdrop);
  };

  /**
   * Show backdrop and lock body scroll
   */
  #showBackdrop = () => {
    if (!this.#isTouchDevice || !this.#backdrop) return;

    const backdrop = this.#backdrop;
    requestAnimationFrame(() => {
      backdrop.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
  };

  /**
   * Hide backdrop and unlock body scroll
   */
  #hideBackdrop = () => {
    if (!this.#isTouchDevice || !this.#backdrop) return;

    this.#backdrop.classList.remove('active');
    document.body.style.overflow = '';
  };

  /**
   * Remove backdrop element from DOM
   */
  #removeBackdrop = () => {
    if (this.#backdrop && this.#backdrop.parentNode) {
      this.#backdrop.parentNode.removeChild(this.#backdrop);
      this.#backdrop = null;
    }
    document.body.style.overflow = '';
  };
}

if (!customElements.get('lindy-header-menu')) {
  customElements.define('lindy-header-menu', LindyHeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.getAttribute('ref') === 'menuitem') {
    return element instanceof HTMLElement ? element : null;
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
